/**
 * ParentViewModal — Модальное окно с деталями родителя.
 *
 * Показывает ФИО/email, телефон, список детей (с привязкой/отвязкой), центры.
 */
import React, { useState, useCallback, useEffect } from 'react';
import {
  Modal, Stack, Text, Badge, Group, Box, Divider, Button, Select, ActionIcon,
} from '@mantine/core';
import {
  IconSchool, IconBuilding, IconPhone, IconMail, IconPlus, IconTrash,
  IconSend, IconCopy, IconCheck, IconKey,
} from '@tabler/icons-react';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { CopyButton, Tooltip, Paper, TextInput } from '@mantine/core';
import tokens from '../../theme/designTokens';
import { getStudents, linkParent, unlinkParent } from '../../services/api/studentsService';
import ResetParentPasswordModal from './ResetParentPasswordModal';

const ParentViewModal = ({ opened, onClose, parent, onEdit, onDataChange }) => {
  const [addChildOpen, setAddChildOpen] = useState(false);
  const [childOptions, setChildOptions] = useState([]);
  const [selectedChildId, setSelectedChildId] = useState(null);
  const [linkLoading, setLinkLoading] = useState(false);

  // Инвайт в ЛК
  const [inviteStatus, setInviteStatus] = useState(null); // { status, invite }
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteUrl, setInviteUrl] = useState(null);

  // Сброс пароля
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);

  // Загрузка списка учеников для привязки
  const loadStudents = useCallback(async () => {
    try {
      const data = await getStudents({ pageSize: 200, isActive: true });
      const linkedIds = new Set((parent?.students || []).map((s) => s.id));
      setChildOptions(
        (data.items || [])
          .filter((s) => !linkedIds.has(s.id))
          .map((s) => {
            const groups = (s.study_groups || []).map((g) =>
              `${g.name}${g.organization_name ? ` (${g.organization_name})` : ''}`
            ).join(', ');
            return {
              value: String(s.id),
              label: s.full_name || s.first_name,
              description: groups || 'Не записан в группы',
            };
          })
      );
    } catch (err) {
      console.error('Failed to load students:', err);
    }
  }, [parent?.students]);

  useEffect(() => {
    if (addChildOpen) loadStudents();
  }, [addChildOpen, loadStudents]);

  const handleLinkChild = async () => {
    if (!selectedChildId || !parent) return;
    setLinkLoading(true);
    try {
      await linkParent(parseInt(selectedChildId), parent.id);
      notifications.show({ title: 'Успешно', message: 'Ребёнок добавлен', color: 'green' });
      setAddChildOpen(false);
      setSelectedChildId(null);
      if (onDataChange) onDataChange();
    } catch (err) {
      notifications.show({ title: 'Ошибка', message: err.message, color: 'red' });
    } finally {
      setLinkLoading(false);
    }
  };

  const handleUnlinkChild = (student) => {
    modals.openConfirmModal({
      title: 'Удалить ребёнка',
      centered: true,
      children: <Text size="sm">Удалить «{student.full_name || student.first_name}» от родителя «{parent?.email}»?</Text>,
      labels: { confirm: 'Удалить', cancel: 'Отмена' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await unlinkParent(student.id, parent.id);
          notifications.show({ title: 'Успешно', message: 'Ребёнок удалён', color: 'green' });
          if (onDataChange) onDataChange();
        } catch (err) {
          notifications.show({ title: 'Ошибка', message: err.message, color: 'red' });
        }
      },
    });
  };

  // Загружаем статус инвайта при открытии модалки
  useEffect(() => {
    if (!opened || !parent) return;
    setInviteUrl(null);
    fetch(`/api/parents/${parent.id}/invite`, { credentials: 'include' })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (data) setInviteStatus(data); })
      .catch(() => {});
  }, [opened, parent]);

  const handleGenerateInvite = async () => {
    setInviteLoading(true);
    try {
      const res = await fetch(`/api/parents/${parent.id}/invite`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Ошибка');
      }
      const data = await res.json();
      setInviteUrl(data.invite_url);
      setInviteStatus({ status: 'pending', invite: data });
      notifications.show({ title: 'Ссылка создана', message: 'Скопируйте и отправьте родителю', color: 'green' });
    } catch (err) {
      notifications.show({ title: 'Ошибка', message: err.message, color: 'red' });
    } finally {
      setInviteLoading(false);
    }
  };

  if (!parent) return null;

  const students = parent.students || [];
  const organizations = parent.organizations || [];

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="sm">
          <Text fw={600}>{parent.full_name || parent.email}</Text>
          <Badge size="sm" variant="filled" color={parent.is_active ? 'green' : 'gray'} radius="xl">
            {parent.is_active ? 'Активен' : 'Неактивен'}
          </Badge>
        </Group>
      }
      size="lg"
      centered
    >
      <Stack gap="md">
        {/* Контактные данные */}
        <Group gap="lg" wrap="wrap">
          <Box>
            <Text size="xs" c="dimmed">Email</Text>
            <Group gap={4}>
              <IconMail size={14} color={tokens.colors.neutral[6]} />
              <Text size="sm" fw={500}>{parent.email}</Text>
            </Group>
          </Box>
          {parent.phone && (
            <Box>
              <Text size="xs" c="dimmed">Телефон</Text>
              <Group gap={4}>
                <IconPhone size={14} color={tokens.colors.neutral[6]} />
                <Text size="sm" fw={500}>{parent.phone}</Text>
              </Group>
            </Box>
          )}
          {(parent.first_name || parent.last_name) && (
            <Box>
              <Text size="xs" c="dimmed">ФИО</Text>
              <Text size="sm" fw={500}>{[parent.last_name, parent.first_name].filter(Boolean).join(' ')}</Text>
            </Box>
          )}
        </Group>

        {/* Центры */}
        {organizations.length > 0 && (
          <Box>
            <Text size="sm" fw={600} mb="xs">
              <Group gap={4}><IconBuilding size={16} /> Центры ({organizations.length})</Group>
            </Text>
            <Group gap="xs">
              {organizations.map((o) => (
                <Badge key={o.id} variant="filled" color="blue" radius="xl" size="md">{o.name}</Badge>
              ))}
            </Group>
          </Box>
        )}

        <Divider />

        {/* Дети */}
        <Box>
          <Group justify="space-between" mb="sm">
            <Text size="sm" fw={600}>
              <Group gap={4}><IconSchool size={16} /> Дети ({students.length})</Group>
            </Text>
            <Button variant="light" size="xs" leftSection={<IconPlus size={14} />} onClick={() => setAddChildOpen(true)}>
              Добавить
            </Button>
          </Group>

          {/* Форма привязки ребёнка */}
          {addChildOpen && (
            <Box mb="sm" p="sm" style={{
              borderRadius: 12,
              border: `1px solid ${tokens.colors.neutral[3]}`,
              backgroundColor: tokens.colors.neutral[1],
            }}>
              <Group gap="sm">
                <Select
                  placeholder="Поиск ребёнка по имени..."
                  data={childOptions}
                  value={selectedChildId}
                  onChange={setSelectedChildId}
                  searchable
                  renderOption={({ option }) => (
                    <Box>
                      <Text size="sm" fw={500}>{option.label}</Text>
                      {option.description && <Text size="xs" c="dimmed">{option.description}</Text>}
                    </Box>
                  )}
                  style={{ flex: 1 }}
                  radius="xl"
                />
                <Button size="sm" onClick={handleLinkChild} loading={linkLoading} disabled={!selectedChildId}>
                  Добавить
                </Button>
                <Button variant="subtle" size="sm" color="gray" onClick={() => { setAddChildOpen(false); setSelectedChildId(null); }}>
                  Отмена
                </Button>
              </Group>
            </Box>
          )}

          {students.length > 0 ? (
            <Stack gap="xs">
              {students.map((s) => {
                const orgNames = [...new Set(
                  (s.study_groups || [])
                    .map((g) => g.organization_name)
                    .filter(Boolean)
                )];
                return (
                  <Group key={s.id} justify="space-between">
                    <Group gap="sm">
                      <Text size="sm" fw={500}>
                        {[s.first_name, s.last_name].filter(Boolean).join(' ')}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {orgNames.length > 0 ? orgNames.join(', ') : 'Нет центров'}
                      </Text>
                    </Group>
                    <ActionIcon variant="subtle" color="red" size="sm" onClick={() => handleUnlinkChild(s)}>
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Group>
                );
              })}
            </Stack>
          ) : (
            <Text size="sm" c="dimmed" ta="center" py="sm">Нет привязанных детей</Text>
          )}
        </Box>

        {/* Приглашение в ЛК */}
        <Divider />
        <Box>
          <Text size="sm" fw={600} mb="xs">
            <Group gap={4}><IconSend size={16} /> Доступ к Личному кабинету</Group>
          </Text>

          {inviteStatus?.status === 'activated' ? (
            <Badge color="green" variant="light" radius="xl" size="md">
              ЛК активирован
            </Badge>
          ) : inviteUrl || inviteStatus?.invite?.invite_url ? (
            <Stack gap="xs">
              <Badge color="orange" variant="light" radius="xl" size="sm">
                Ожидает активации
              </Badge>
              <Group gap="xs">
                <TextInput
                  value={inviteUrl || inviteStatus?.invite?.invite_url || ''}
                  readOnly
                  size="xs"
                  radius="xl"
                  style={{ flex: 1 }}
                  styles={{ input: { fontFamily: 'monospace', fontSize: 11 } }}
                />
                <CopyButton value={inviteUrl || inviteStatus?.invite?.invite_url || ''}>
                  {({ copied, copy }) => (
                    <Tooltip label={copied ? 'Скопировано' : 'Скопировать'}>
                      <ActionIcon variant="subtle" onClick={copy} size="sm">
                        {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                      </ActionIcon>
                    </Tooltip>
                  )}
                </CopyButton>
              </Group>
              <Text size="xs" c="dimmed">
                Отправьте эту ссылку родителю. По ней он задаст пароль и войдёт в ЛК.
              </Text>
            </Stack>
          ) : (
            <Button
              variant="light"
              size="sm"
              leftSection={<IconSend size={14} />}
              loading={inviteLoading}
              onClick={handleGenerateInvite}
            >
              Сгенерировать ссылку-приглашение
            </Button>
          )}
        </Box>

        {/* Кнопки */}
        <Group justify="flex-end" mt="sm">
          <Button
            variant="light"
            color="red"
            leftSection={<IconKey size={14} />}
            onClick={() => setResetPasswordOpen(true)}
          >
            Сбросить пароль
          </Button>
          {onEdit && <Button variant="light" onClick={() => onEdit(parent)}>Редактировать</Button>}
          <Button variant="default" onClick={onClose}>Закрыть</Button>
        </Group>
      </Stack>

      {/* Модалка сброса пароля */}
      <ResetParentPasswordModal
        opened={resetPasswordOpen}
        onClose={() => setResetPasswordOpen(false)}
        parent={parent}
      />
    </Modal>
  );
};

export default ParentViewModal;
